import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';
import NewsletterSignup from '@/components/NewsletterSignup';
import { track } from '@/lib/track';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'Models', path: '/models' },
    { name: 'Build Your Van', path: '/build' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Van Rentals', path: '/#van-rentals' },
    { name: 'Showroom', path: '/showroom' },
    { name: 'Blog', path: '/blog' },
  ];

  const companyLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' },
    { name: 'Privacy Policy', path: '/privacy' },
    { name: 'Terms of Service', path: '/terms' },
  ];

  const services = [
    { name: 'Luxury Van Conversions', path: '/luxury-van-conversions' },
    { name: 'Party Van Conversions', path: '/party-van-conversions' },
    { name: 'Sprinter Conversions', path: '/mercedes-sprinter-van-conversions' },
    { name: 'Executive & Limo Vans', path: '/executive-limo-van-conversions' },
    { name: 'Family Travel Vans', path: '/family-travel-van-conversions' },
    { name: 'Shuttle Conversions', path: '/shuttle-van-conversions' },
    { name: 'Texas Van Builder', path: '/texas-van-conversion-company' },
    { name: 'DFW Van Conversions', path: '/dallas-fort-worth-van-conversions' },
  ];

  return (
    <footer data-testid="main-footer" className="bg-navy text-white relative overflow-hidden">
      {/* Noise texture */}
      <div className="noise-overlay absolute inset-0 pointer-events-none" />
      
      {/* Top CTA Section */}
      <div className="border-b border-white/10">
        <div className="container mx-auto px-6 lg:px-12 py-16">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div>
              <h3 className="font-display text-3xl lg:text-4xl font-semibold mb-2">
                Ready to Build Your <span className="text-gold">Legend</span>?
              </h3>
              <p className="text-white/70 text-lg">
                Start your custom van journey today.
              </p>
            </div>
            <div className="flex gap-4">
              <Link to="/build">
                <button 
                  data-testid="footer-cta-quote"
                  className="btn-gold"
                >
                  Get a Quote
                </button>
              </Link>
              <Link to="/contact">
                <button 
                  data-testid="footer-cta-contact"
                  className="bg-transparent border border-white text-white px-8 py-4 font-bold tracking-widest hover:bg-white hover:text-navy transition-all duration-300 rounded-none uppercase text-sm"
                >
                  Contact Us
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="container mx-auto px-6 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6">
              <img 
                src="/logo.png" 
                alt="Legend Conversion" 
                className="h-12 w-auto"
              />
            </Link>
            <p className="text-white/70 mb-6 max-w-sm leading-relaxed">
              Founded in 2023 and built on 31+ years of conversion industry experience. 
              Factory-direct custom luxury vans — no dealership in between.
            </p>
            <div className="space-y-3">
              <a 
                href="tel:6823691500" 
                onClick={() => track('phone_click', { location: 'footer' })}
                className="flex items-center gap-3 text-white/80 hover:text-gold transition-colors"
              >
                <Phone className="w-5 h-5 text-gold" />
                (682) 369-1500
              </a>
              <a 
                href="mailto:sales@legendconversion.com" 
                className="flex items-center gap-3 text-white/80 hover:text-gold transition-colors"
              >
                <Mail className="w-5 h-5 text-gold" />
                sales@legendconversion.com
              </a>
              <div className="flex items-start gap-3 text-white/80">
                <MapPin className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <span>4209 Longhorn Dr, Alvarado, TX 76009<br /><span className="text-white/60 text-sm">Mon&ndash;Fri 7:00 AM &ndash; 4:30 PM</span></span>
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-display text-lg font-semibold mb-6 text-gold">Services</h4>
            <ul className="space-y-3">
              {services.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    data-testid={`footer-service-${link.path.slice(1)}`}
                    className="text-white/70 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display text-lg font-semibold mb-6 text-gold">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-white/70 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-display text-lg font-semibold mb-6 text-gold">Company</h4>
            <ul className="space-y-3">
              {companyLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-white/70 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter Signup */}
          <div>
            <NewsletterSignup source="footer" variant="compact" />
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-16 pt-8 border-t border-white/10 flex items-center justify-center">
          <p className="text-white/50 text-sm">
            &copy; {currentYear} Legend Conversion Corp. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
